/**
 * @file
 * Types and helpers for RTK query cache invalidation.
 */

/* prettier-ignore */
export const TagType = {
  Automation: "Automation",
  AutomationArtifact: "AutomationArtifact",
  AutomationRun: "AutomationRun",
  BasicTeammate: "BasicTeammate",
  ContactsAndRecordingsRelatedToRecording: "ContactsAndRecordingsRelatedToRecording",
  CreditUsage: "CreditUsage",
  ComputedFieldDefinition: "ComputedFieldDefinition",
  CustomField: "CustomField",
  DataExtractionFieldDefinition: "DataExtractionFieldDefinition",
  Form: "Form",
  GenesysIntegration: "GenesysIntegration",
  Group: "Group",
  InterviewSectionDefinition: "InterviewSectionDefinition",
  JobType: "JobType",
  KnowledgeBase: "KnowledgeBase",
  KnowledgeBaseAttachment: "KnowledgeBaseAttachment",
  KnowledgeBaseSyncIntegration: "KnowledgeBaseSyncIntegration",
  LicenseUtilization: "LicenseUtilization",
  MessageTemplate: "MessageTemplate",
  MinimalPlaybook: "MinimalPlaybook",
  Playbook: "Playbook",
  PlaybookRoutingRules: "PlaybookRoutingRules",
  PlaybookSampleToolResult: "PlaybookSampleToolResult",
  PlaybookTestResult: "PlaybookTestResult",
  PlaybookTestRun: "PlaybookTestRun",
  PlaybookTestSpec: "PlaybookTestSpec",
  PlaybookTestSpecResult: "PlaybookTestSpecResult",
  Project: "Project",
  RecordingAssessmentFeedback: "RecordingAssessmentFeedback",
  RecordingS3IntakeIntegration: "RecordingS3IntakeIntegration",
  RecruitingInsights: "RecruitingInsights",
  SchedulingConfiguration: "SchedulingConfiguration",
  Signal: "Signal",
  Teammate: "Teammate",
  Topic: "Topic",
  TeammateAgent: "TeammateAgent",
  TeammateCallOutline: "TeammateCallOutline",
  TeammateChatMessages: "TeammateChatMessages",
  TeammateCoachingManagerInput: "TeammateCoachingManagerInput",
  TeammatePerformance: "TeammatePerformance",
  TeammatePerformanceGrading: "TeammatePerformanceGrading",
  TeammatePerformanceSummary: "TeammatePerformanceSummary",
  TeammatePlaybookCriteriaScores: "TeammatePlaybookCriteriaScores",
  TeamBenchmarkScores: "TeamBenchmarkScores",
  TeamPerformancePage: "TeamPerformancePage",
  Tool: "Tool",
  UserCustomFieldDefinition: "UserCustomFieldDefinition",
} as const;

export const allTagTypes = [
  TagType.Automation,
  TagType.AutomationArtifact,
  TagType.AutomationRun,
  TagType.BasicTeammate,
  TagType.ContactsAndRecordingsRelatedToRecording,
  TagType.CreditUsage,
  TagType.ComputedFieldDefinition,
  TagType.CustomField,
  TagType.DataExtractionFieldDefinition,
  TagType.Form,
  TagType.GenesysIntegration,
  TagType.Group,
  TagType.InterviewSectionDefinition,
  TagType.JobType,
  TagType.KnowledgeBase,
  TagType.KnowledgeBaseAttachment,
  TagType.KnowledgeBaseSyncIntegration,
  TagType.LicenseUtilization,
  TagType.MessageTemplate,
  TagType.MinimalPlaybook,
  TagType.Playbook,
  TagType.PlaybookRoutingRules,
  TagType.PlaybookSampleToolResult,
  TagType.PlaybookTestResult,
  TagType.PlaybookTestRun,
  TagType.PlaybookTestSpec,
  TagType.PlaybookTestSpecResult,
  TagType.Project,
  TagType.RecordingAssessmentFeedback,
  TagType.RecordingS3IntakeIntegration,
  TagType.RecruitingInsights,
  TagType.SchedulingConfiguration,
  TagType.Signal,
  TagType.Teammate,
  TagType.TeammateAgent,
  TagType.TeammateChatMessages,
  TagType.TeammateCallOutline,
  TagType.TeammateCoachingManagerInput,
  TagType.TeammatePerformance,
  TagType.TeammatePerformanceGrading,
  TagType.TeammatePerformanceSummary,
  TagType.TeammatePlaybookCriteriaScores,
  TagType.TeamBenchmarkScores,
  TagType.Tool,
  TagType.Topic,
  TagType.UserCustomFieldDefinition,
];

type TagType = (typeof allTagTypes)[number];

type EntityOrId = { id: string } | string;

/** Builds a cache tag for the given entity. */
export function buildEntityTag<T>(type: T, entityOrId: EntityOrId) {
  const id = typeof entityOrId === "string" ? entityOrId : entityOrId.id;
  return { type, id };
}

/**
 * Builds a cache tag for a list of entities.
 *
 * @param type - The cache tag type.
 * @param filteredBy - If this is a list of all entities of this type, provide an empty array. If the list is filtered (such as the children of a parent), provide an array of the filter conditions (such as parent ID).
 */
export function buildListTag<T>(type: T, filteredBy: readonly string[]) {
  const listIds = [...filteredBy, "LIST"];
  return { type, id: listIds.join("/") };
}

/**
 * Helper for the `providesTags` option for a query of a list of entities.
 *
 * @param type - The cache tag type.
 * @param entitiesOrIds - The entities in the query result, if any.
 * @param options - Optional parameters.
 *   - `filteredBy`: Filter criteria for this list, if any. See `buildListTag`.
 */
export function getTagsProvidedByList<T>(
  type: T,
  entitiesOrIds: readonly EntityOrId[] | undefined,
  options?: {
    filteredBy?: readonly string[];
  },
) {
  return [
    // Tag for the list itself.
    buildListTag(type, options?.filteredBy ?? []),
    // Tag for each entity in the result.
    ...(entitiesOrIds
      ? entitiesOrIds.map((entityOrId) => buildEntityTag(type, entityOrId))
      : []),
  ];
}

/**
 * Helper for the `providesTags` option for a query of a single entity.
 *
 * @param type - The cache tag type.
 * @param id - The ID of the entity in the query result.
 */
export function getTagsProvidedByEntity<T>(type: T, id: string) {
  return [
    // Tag for the entity itself only.
    buildEntityTag(type, id),
  ];
}

/**
 * Helper for the `invalidatesTags` option for a create mutation.
 *
 * @param type - The cache tag type.
 * @param options - Optional parameters.
 *  - `inLists`: Filter criteria for any list that this entity may have been added to as a result of the create. See `buildListTag`.
 */
export function getTagsInvalidatedByCreate<T>(
  type: T,
  options?: {
    inLists?: { filteredBy: readonly string[] }[];
  },
) {
  return [
    // Always invalidate the list of all entities.
    buildListTag(type, []),
    // Invalidate any filtered lists that the entity has been added to.
    ...(options?.inLists ?? []).map(({ filteredBy }) =>
      buildListTag(type, filteredBy),
    ),
  ];
}

/**
 * Helper for the `invalidatesTags` option for an update mutation.
 *
 * @param type - The cache tag type.
 * @param id - The ID of the entity being updated.
 * @param options - Optional parameters.
 *  - `inLists`: Filter criteria for any list that this entity may have been added to as a result of the update. See `buildListTag`.
 */
export function getTagsInvalidatedByUpdate<T>(
  type: T,
  id: string,
  options?: {
    inLists?: { filteredBy: readonly string[] }[];
  },
) {
  return [
    // Invalidate any filtered lists that the entity may have been added to.
    ...(options?.inLists ?? []).map(({ filteredBy }) =>
      buildListTag(type, filteredBy),
    ),
    // Invalidate the entity itself, including any lists that it previously appeared in.
    buildEntityTag(type, id),
  ];
}

export function getTagsInvalidatedByDelete<T>(type: T, id: string) {
  return [
    // Invalidate the entity itself, including any lists that it previously appeared in.
    buildEntityTag(type, id),
  ];
}
